/**
 * /diag — connectivity test for the China pilot. Needs no account. Runs a fixed sequence of
 * synthetic requests and shows a short code to send back; only timings are stored.
 */
import { useState } from 'react';
import { browserUiLocale } from '../i18n.ts';
import { LanguageSwitch } from './LanguageStep.tsx';

type Lang = 'sv-SE' | 'zh-Hans' | 'en';
const TXT = {
  title: { 'zh-Hans': '连接测试', 'sv-SE': 'Anslutningstest', en: 'Connection test' },
  intro: {
    'zh-Hans': '此测试检查本聊天工具在您的网络上是否运行良好。大约需要 30 秒，只发送测试数据，不会读取您手机上的任何内容。',
    'sv-SE': 'Testet kontrollerar hur bra chattverktyget fungerar på ditt nätverk. Det tar cirka 30 sekunder och skickar bara testdata – inget från din telefon läses.',
    en: 'This test checks how well the chat tool works on your network. It takes about 30 seconds and only sends test data — nothing on your device is read.',
  },
  label: { 'zh-Hans': '名字和网络（可选，例如"李伟 - 移动 5G"）', 'sv-SE': 'Namn och nätverk (frivilligt, t.ex. "Li – China Mobile 5G")', en: 'Name and network (optional, e.g. "Li – China Mobile 5G")' },
  start: { 'zh-Hans': '开始测试', 'sv-SE': 'Starta testet', en: 'Start the test' },
  running: { 'zh-Hans': '正在测试…', 'sv-SE': 'Testar…', en: 'Testing…' },
  again: { 'zh-Hans': '再测一次', 'sv-SE': 'Testa igen', en: 'Run again' },
  send: { 'zh-Hans': '发送结果', 'sv-SE': 'Skicka resultatet', en: 'Send the result' },
  code: { 'zh-Hans': '完成！请把这个代码发给我们：', 'sv-SE': 'Klart! Skicka den här koden till oss:', en: 'Done! Please send us this code:' },
  failedSend: { 'zh-Hans': '无法发送结果。请截图发给我们。', 'sv-SE': 'Resultatet kunde inte skickas. Ta en skärmdump och skicka den till oss.', en: 'Could not send the result. Please take a screenshot and send it to us.' },
  ok: { 'zh-Hans': '正常', 'sv-SE': 'OK', en: 'OK' },
  fail: { 'zh-Hans': '失败', 'sv-SE': 'Misslyckades', en: 'Failed' },
} as const;
const STEP_NAMES: Record<string, Record<Lang, string>> = {
  page: { 'zh-Hans': '打开页面', 'sv-SE': 'Sidan laddas', en: 'Page load' },
  api: { 'zh-Hans': '服务器响应（20 次）', 'sv-SE': 'Serversvar (20 st)', en: 'Server replies (20×)' },
  down200: { 'zh-Hans': '下载 200 KB', 'sv-SE': 'Hämta 200 kB', en: 'Download 200 kB' },
  down1m: { 'zh-Hans': '下载 1 MB', 'sv-SE': 'Hämta 1 MB', en: 'Download 1 MB' },
  up200: { 'zh-Hans': '上传 200 KB', 'sv-SE': 'Skicka 200 kB', en: 'Upload 200 kB' },
  ws: { 'zh-Hans': '实时连接', 'sv-SE': 'Realtidsanslutning', en: 'Real-time connection' },
  wsEcho: { 'zh-Hans': '实时消息（20 次）', 'sv-SE': 'Realtidsmeddelanden (20 st)', en: 'Real-time messages (20×)' },
  wsReconnect: { 'zh-Hans': '重新连接', 'sv-SE': 'Återanslutning', en: 'Reconnect' },
  poll: { 'zh-Hans': '备用连接', 'sv-SE': 'Reservanslutning', en: 'Fallback connection' },
};

interface Result {
  step: string;
  ok: boolean;
  ms?: number | null;
  p50?: number | null;
  p95?: number | null;
  detail?: string | null;
}

const now = () => performance.now();
const pct = (xs: number[], p: number) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return Math.round(s[Math.min(s.length - 1, Math.floor((p / 100) * s.length))]!);
};
async function timed<T>(fn: () => Promise<T>, timeoutMs = 20_000): Promise<{ ms: number; value: T }> {
  const t0 = now();
  const value = await Promise.race([fn(), new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), timeoutMs))]);
  return { ms: Math.round(now() - t0), value };
}
const fetchOk = async (url: string, init?: RequestInit) => {
  const r = await fetch(url, { cache: 'no-store', credentials: 'same-origin', ...init });
  if (!r.ok) throw new Error(`http_${r.status}`);
  return r;
};

function wsOpen(): Promise<WebSocket> {
  return new Promise((res, rej) => {
    const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/diag/ws`);
    const timer = setTimeout(() => {
      ws.close();
      rej(new Error('timeout'));
    }, 10_000);
    ws.onopen = () => {
      clearTimeout(timer);
      res(ws);
    };
    ws.onerror = () => {
      clearTimeout(timer);
      rej(new Error('ws_error'));
    };
  });
}
function wsEcho(ws: WebSocket, msg: string): Promise<void> {
  return new Promise((res, rej) => {
    const timer = setTimeout(() => rej(new Error('timeout')), 10_000);
    ws.onmessage = () => {
      clearTimeout(timer);
      res();
    };
    ws.send(msg);
  });
}

async function runAll(onStep: (r: Result) => void): Promise<Result[]> {
  const out: Result[] = [];
  const add = (r: Result) => {
    out.push(r);
    onStep(r);
  };
  const err = (e: unknown) => (e instanceof Error ? e.message : 'error').slice(0, 120);

  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  add({ step: 'page', ok: true, ms: nav ? Math.round(nav.loadEventEnd || nav.domContentLoadedEventEnd) : null, detail: nav ? `ttfb ${Math.round(nav.responseStart)} ms` : null });

  const times: number[] = [];
  let fails = 0;
  for (let i = 0; i < 20; i++) {
    try {
      times.push((await timed(() => fetchOk(`/api/diag/ping?i=${i}`), 10_000)).ms);
    } catch {
      fails++;
    }
  }
  add({ step: 'api', ok: fails === 0, p50: pct(times, 50), p95: pct(times, 95), detail: fails ? `${fails}/20 failed` : null });

  for (const [step, size] of [['down200', 200_000], ['down1m', 1_000_000]] as const) {
    try {
      const { ms } = await timed(async () => (await fetchOk(`/api/diag/blob?size=${size}&r=${Math.random()}`)).arrayBuffer(), 60_000);
      add({ step, ok: true, ms, detail: `${Math.round((size * 8) / ms)} kbit/s` });
    } catch (e) {
      add({ step, ok: false, detail: err(e) });
    }
  }
  try {
    const body = new Uint8Array(200_000);
    const { ms } = await timed(() => fetchOk('/api/diag/upload', { method: 'POST', body, headers: { 'content-type': 'application/octet-stream', 'x-tc': '1' } }), 60_000);
    add({ step: 'up200', ok: true, ms, detail: `${Math.round((200_000 * 8) / ms)} kbit/s` });
  } catch (e) {
    add({ step: 'up200', ok: false, detail: err(e) });
  }

  let ws: WebSocket | null = null;
  try {
    const r = await timed(wsOpen, 12_000);
    ws = r.value;
    add({ step: 'ws', ok: true, ms: r.ms });
  } catch (e) {
    add({ step: 'ws', ok: false, detail: err(e) });
  }
  if (ws) {
    const echoes: number[] = [];
    let wsFails = 0;
    for (let i = 0; i < 20; i++) {
      try {
        echoes.push((await timed(() => wsEcho(ws!, `ping ${i}`), 10_000)).ms);
      } catch {
        wsFails++;
      }
    }
    add({ step: 'wsEcho', ok: wsFails === 0, p50: pct(echoes, 50), p95: pct(echoes, 95), detail: wsFails ? `${wsFails}/20 failed` : null });
    ws.close();
    try {
      const r = await timed(wsOpen, 12_000);
      r.value.close();
      add({ step: 'wsReconnect', ok: true, ms: r.ms });
    } catch (e) {
      add({ step: 'wsReconnect', ok: false, detail: err(e) });
    }
  }
  try {
    const { ms } = await timed(() => fetchOk('/api/diag/poll?wait=2000'), 30_000);
    add({ step: 'poll', ok: true, ms, detail: `+${Math.max(0, ms - 2000)} ms` });
  } catch (e) {
    add({ step: 'poll', ok: false, detail: err(e) });
  }
  return out;
}

export function DiagPage() {
  const initial = browserUiLocale();
  const [lang, setLang] = useState<Lang>(initial === 'sv-SE' || initial === 'zh-Hans' ? initial : 'en');
  const [label, setLabel] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [state, setState] = useState<'idle' | 'running' | 'done'>('idle');
  const [code, setCode] = useState<string | null>(null);
  const [sendFailed, setSendFailed] = useState(false);
  const tx = (k: keyof typeof TXT) => TXT[k][lang];

  const start = async () => {
    setResults([]);
    setCode(null);
    setSendFailed(false);
    setState('running');
    const all = await runAll((r) => setResults((cur) => [...cur, r]));
    setState('done');
    try {
      const network = (navigator as unknown as { connection?: { effectiveType?: string; type?: string } }).connection;
      const r = await fetch('/api/diag/report', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-tc': '1' },
        body: JSON.stringify({ label: label || null, network: network ? `${network.type ?? ''} ${network.effectiveType ?? ''}`.trim() || null : null, results: all }),
      });
      if (!r.ok) throw new Error();
      setCode(((await r.json()) as { code: string }).code);
    } catch {
      setSendFailed(true);
    }
  };

  const fmt = (r: Result) => (r.p50 != null ? `${r.p50} / ${r.p95} ms` : r.ms != null ? `${r.ms} ms` : '');
  return (
    <main className="auth">
      <div className="auth-card diag">
        <LanguageSwitch value={lang} onChange={(l) => setLang(l as Lang)} />
        <h1>{tx('title')}</h1>
        <p className="muted">{tx('intro')}</p>
        <label className="field">
          <span>{tx('label')}</span>
          <input value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} disabled={state === 'running'} />
        </label>
        <button className="btn primary" onClick={() => void start()} disabled={state === 'running'}>
          {state === 'running' ? tx('running') : state === 'done' ? tx('again') : tx('start')}
        </button>
        {results.length > 0 && (
          <table className="table diag-table">
            <tbody>
              {results.map((r) => (
                <tr key={r.step}>
                  <td>{STEP_NAMES[r.step]?.[lang] ?? r.step}</td>
                  <td className={r.ok ? 'ok' : 'bad'}>{r.ok ? tx('ok') : tx('fail')}</td>
                  <td className="num">{fmt(r)}</td>
                  <td className="muted small">{r.detail ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {code && (
          <p className="diag-code" role="status">
            {tx('code')} <strong>{code}</strong>
          </p>
        )}
        {sendFailed && <p className="form-error" role="alert">{tx('failedSend')}</p>}
      </div>
    </main>
  );
}
